import { Store } from "../core/state";
import { applyStyles, clearElement, el } from "../core/dom";
import { createMediaQuery, type MediaQuerySubscription } from "../core/media-query";
import { injectCheckboxStyles } from "../styles/inject";
import { styles } from "../styles/modal";
import { renderLanguageSelector, type LanguageSelectorRefs } from "../modal/language-selector";
import { setupKeyboardTrap, type KeyboardTrapCleanup } from "../modal/keyboard";
import { injectNoticeFontStyles, resolveNoticeFontFamily } from "../modal/notice-font";
import { TTSManager } from "../modal/tts";
import {
  fetchNoticeConsentStatusForScope,
  isNoticeFullyConsented,
} from "../api/notice-consent-status";
import { toLanguageCode } from "../api/language";
import { resolveEndpointOptions, type ResolvedEndpoints } from "../shared/endpoints";
import { NOTICE_FONT_ROOT_ATTRIBUTE } from "../constants";
import type { TranslationObject } from "../api/types";
import type { AcceptMode } from "../types";
import {
  createOtp,
  fetchAssistedNotice,
  fetchAssistedTTSAudioUrls,
  submitAssistedConsent,
  verifyOtp,
} from "./api";
import {
  ASSISTED_CONFIG,
  ASSISTED_DEFAULT_PRIMARY_COLOR,
  ASSISTED_HOST_ATTRIBUTE,
  ASSISTED_IDS,
  FOCUS_KEY_ATTRIBUTE,
  FOCUS_KEYS,
  MOBILE_QUERY,
} from "./constants";
import { makeStaticT } from "./i18n";
import { speakerIcon } from "./icons";
import { renderDpoFooter, renderNoticeText, renderPurposeHeading, renderPurposes } from "./notice-view";
import {
  applyOtpDigit,
  applyOtpPaste,
  areAllRequiredElementsChecked,
  backspaceFocusIndex,
  buildAssistedSubmitPurposes,
  buildPreselection,
  emptyOtpDigits,
  isAlreadyConsentedError,
  isContactValid,
  isOtpComplete,
  otpChannel,
  otpRecipient,
  purposeHidingElement,
  resolvePurposePreselection,
  sanitiseMobile,
  selectionForMode,
  toggleDataElementSelection,
  togglePurposeSelection,
  verifyFailureKey,
} from "./selection";
import { assistedStyles, injectAssistedStyles } from "./styles";
import { availableLanguages, isDefaultLanguage, normaliseLanguage } from "./theme";
import { buildAssistedLook } from "./look";
import { DARK_SCHEME_QUERY } from "../appearance/constants";
import { NoticeCustomCss } from "../appearance/custom-css";
import { part } from "../appearance/parts";
import { applyCssVariables } from "../appearance/theme-vars";
import { purposesInRenderOrder } from "../core/productMatrix";
import { logoRowJustify } from "../modal/layout";
import { getProductPolicyLinks } from "../modal/productPolicies";
import { otpBoxStyle, primaryAction, renderFooter, renderResend, renderVerifyPanel } from "./verify-view";
import type {
  AssistedActiveConfig,
  AssistedHandlers,
  AssistedOptions,
  AssistedState,
  AssistedViewContext,
  StaticTranslate,
  VerifiedOtp,
} from "./types";

const PATCHABLE_KEYS: ReadonlySet<keyof AssistedState> = new Set<keyof AssistedState>([
  "mobile",
  "email",
  "otpDigits",
  "resendCountdown",
  "ttsRevision",
]);

const initialState = (): AssistedState => ({
  isLoading: true,
  fetchError: null,
  content: null,
  step: "notice",
  acceptMode: "selected",
  selectedPurposes: {},
  selectedDataElements: {},
  collapsedPurposes: {},
  selectedLanguage: "English",
  verifyMethod: "mobile",
  mobile: "",
  email: "",
  otpSent: false,
  otpDigits: emptyOtpDigits(),
  otpUuid: "",
  resendCountdown: 0,
  isSendingOtp: false,
  isVerifying: false,
  createError: null,
  otpError: null,
  ttsRevision: 0,
});

const otpInputId = (index: number): string => `${ASSISTED_IDS.otpInputPrefix}${index}`;

const scrollIntoViewSoft = (target: Element | null): void => {
  if (!target || typeof (target as HTMLElement).scrollIntoView !== "function") return;
  const reduce =
    typeof window.matchMedia === "function" &&
    Boolean(window.matchMedia("(prefers-reduced-motion: reduce)")?.matches);
  (target as HTMLElement).scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "nearest" });
};

export class AssistedController {
  private options: AssistedOptions;
  private readonly endpoints: ResolvedEndpoints;
  private store: Store<AssistedState>;
  private mediaQuery: MediaQuerySubscription;
  private colorScheme: MediaQuerySubscription;
  private customCss = new NoticeCustomCss();
  private overlayEl: HTMLElement | null = null;
  private hostEl: HTMLElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private modalEl: HTMLElement | null = null;
  private middleEl: HTMLElement | null = null;
  private langSelectorRefs: LanguageSelectorRefs | null = null;
  private ttsManager: TTSManager | null = null;
  private keyboardCleanup: KeyboardTrapCleanup | null = null;
  private unsubscribe: (() => void) | null = null;
  private abortController = new AbortController();
  private resendTimer: ReturnType<typeof setInterval> | null = null;
  private pendingFocusId: string | null = null;
  private pendingReveal: string | null = null;
  private previousBodyOverflow: string | null = null;
  private previousHtmlOverflow: string | null = null;
  private otpRequest = 0;
  private verified: VerifiedOtp | null = null;
  private destroyed = false;
  private handlers: AssistedHandlers;

  constructor(options: AssistedOptions) {
    this.options = options;
    this.endpoints = resolveEndpointOptions(options.baseUrl, options.ledgerBaseUrl);
    this.store = new Store<AssistedState>(initialState());
    this.mediaQuery = createMediaQuery(MOBILE_QUERY);
    // Automatic dark mode follows the device's scheme while the notice is open.
    this.colorScheme = createMediaQuery(DARK_SCHEME_QUERY);
    this.handlers = {
      onPurposeToggle: (uuid) => this.handlePurposeToggle(uuid),
      onPurposeCollapse: (uuid) => this.handlePurposeCollapse(uuid),
      onDataElementToggle: (purposeUuid, elementUuid) =>
        this.handleDataElementToggle(purposeUuid, elementUuid),
      onAcceptAll: () => this.handleConfirmStep("all"),
      onAcceptSelected: () => this.handleConfirmStep("selected"),
      onDecline: () => this.handleDecline(),
      onGetOtp: () => this.handleGetOtp(),
      onConfirmOtp: () => {
        void this.handleConfirmOtp();
      },
      onEditSelections: () => this.handleEditSelections(),
      onSwitchMethod: () => this.handleSwitchMethod(),
      onMobileInput: (value) => this.store.setState({ mobile: sanitiseMobile(value) }),
      onEmailInput: (value) => this.store.setState({ email: value }),
      onChangeContact: () => this.handleChangeContact(),
      onResend: () => this.handleResend(),
      onOtpInput: (index, value) => this.handleOtpInput(index, value),
      onOtpKeydown: (index, event) => this.handleOtpKeydown(index, event),
      onOtpPaste: (event) => this.handleOtpPaste(event),
    };
  }

  async init(): Promise<void> {
    this.mount();
    this.unsubscribe = this.store.subscribe((state, prev) => this.handleStateChange(state, prev));
    this.mediaQuery.subscribe(() => this.render());
    this.colorScheme.subscribe(() => this.render());
    this.render();
    await this.fetchNotice();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.abortController.abort();
    this.stopCountdown();
    this.keyboardCleanup?.();
    this.langSelectorRefs?.close();
    this.ttsManager?.destroy();
    this.unsubscribe?.();
    this.store.destroy();
    this.mediaQuery.destroy();
    this.colorScheme.destroy();
    this.customCss.remove();
    if (this.hostEl?.parentNode) {
      this.hostEl.parentNode.removeChild(this.hostEl);
    }
    if (this.previousBodyOverflow !== null) {
      document.body.style.overflow = this.previousBodyOverflow;
    }
    if (this.previousHtmlOverflow !== null) {
      document.documentElement.style.overflow = this.previousHtmlOverflow;
    }
    this.hostEl = null;
    this.shadowRoot = null;
    this.modalEl = null;
    this.middleEl = null;
    this.langSelectorRefs = null;
  }

  private mount(): void {
    this.hostEl = document.createElement("div");
    this.hostEl.setAttribute(ASSISTED_HOST_ATTRIBUTE, "");
    this.hostEl.style.position = "fixed";
    this.hostEl.style.top = "0";
    this.hostEl.style.left = "0";
    this.hostEl.style.width = "0";
    this.hostEl.style.height = "0";
    this.hostEl.style.zIndex = "2147483646";
    document.body.appendChild(this.hostEl);
    this.shadowRoot = this.hostEl.attachShadow({ mode: "open" });

    this.previousBodyOverflow = document.body.style.overflow;
    this.previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    injectCheckboxStyles(this.shadowRoot);
    injectNoticeFontStyles(this.shadowRoot);
    injectAssistedStyles(this.shadowRoot);

    this.ttsManager = new TTSManager(
      () => this.handleTtsChange(),
      this.shadowRoot,
      (params) =>
        fetchAssistedTTSAudioUrls({
          organisationUuid: this.options.organisationUuid,
          workspaceUuid: this.options.workspaceUuid,
          noticeUuid: params.noticeUuid,
          language: params.language,
          baseUrl: this.endpoints.baseUrl,
          signal: params.signal,
        })
    );

    this.overlayEl = el("div", { ...part("overlay"), style: styles.overlay, role: "presentation" });
    this.shadowRoot.appendChild(this.overlayEl);
    this.modalEl = el("div", {
      style: styles.modal,
      role: "dialog",
      "aria-modal": "true",
      [NOTICE_FONT_ROOT_ATTRIBUTE]: "",
    });
    this.shadowRoot.appendChild(this.modalEl);
  }

  private handleTtsChange(): void {
    if (this.destroyed) return;
    const state = this.store.getState();
    const hidingPurpose = state.content
      ? purposeHidingElement(
          state.content.purposes,
          state.collapsedPurposes,
          this.ttsManager?.currentElementId ?? null
        )
      : null;
    this.store.setState(
      hidingPurpose
        ? {
            collapsedPurposes: { ...state.collapsedPurposes, [hidingPurpose]: false },
            ttsRevision: state.ttsRevision + 1,
          }
        : { ttsRevision: state.ttsRevision + 1 }
    );
  }

  private ts(): StaticTranslate {
    return makeStaticT(toLanguageCode(this.store.getState().selectedLanguage) || "en");
  }

  private translate(key: string, fallback: string, itemId?: string): string {
    const { content, selectedLanguage } = this.store.getState();
    if (!content) return fallback;
    if (isDefaultLanguage(selectedLanguage)) {
      if (itemId) return fallback;
      const value = (content as unknown as Record<string, unknown>)[key];
      return typeof value === "string" && value ? value : fallback;
    }
    const map = content.supported_languages_and_translations?.[selectedLanguage] as
      | TranslationObject
      | undefined;
    if (!map) return fallback;
    if (itemId) {
      if (key === "purposes.name" || key === "purposes.description") {
        const purpose = map.purposes?.[itemId];
        if (!purpose) return fallback;
        if (typeof purpose === "string") {
          return key === "purposes.name" ? purpose : fallback;
        }
        return key === "purposes.name" ? purpose.name || fallback : purpose.description || fallback;
      }
      if (key.startsWith("data_elements.")) {
        return map.data_elements?.[itemId] || fallback;
      }
      return fallback;
    }
    const value = (map as unknown as Record<string, unknown>)[key];
    return typeof value === "string" && value ? value : fallback;
  }

  private translateDpo(key: string, fallback: string): string {
    const { content, selectedLanguage } = this.store.getState();
    if (!content?.dpo_info) return fallback;
    const base = (content.dpo_info as unknown as Record<string, string | undefined>)[key] || fallback;
    if (isDefaultLanguage(selectedLanguage)) return base;
    const map = content.supported_languages_and_translations?.[selectedLanguage];
    const translated = (map?.dpo_info as Record<string, string | undefined> | undefined)?.[key];
    return translated || base;
  }

  private async fetchNotice(): Promise<void> {
    try {
      const data = await fetchAssistedNotice({
        organisationUuid: this.options.organisationUuid,
        workspaceUuid: this.options.workspaceUuid,
        noticeUuid: this.options.noticeUuid,
        baseUrl: this.endpoints.baseUrl,
        signal: this.abortController.signal,
      });
      if (this.destroyed) return;
      const activeConfig = data.detail.active_config;
      const preselection = buildPreselection(
        activeConfig.purposes,
        resolvePurposePreselection(activeConfig.purpose_preselection)
      );
      const collapsedPurposes: Record<string, boolean> = {};
      activeConfig.purposes.forEach((purpose) => {
        collapsedPurposes[purpose.uuid] = true;
      });
      this.store.setState({
        content: activeConfig,
        isLoading: false,
        selectedLanguage: normaliseLanguage(activeConfig.default_language),
        collapsedPurposes,
        ...preselection,
      });
      if (this.modalEl) {
        this.keyboardCleanup = setupKeyboardTrap(this.modalEl, () => undefined);
      }
      this.fetchAudio();
    } catch (err) {
      if (this.destroyed || (err instanceof Error && err.name === "AbortError")) return;
      const error = err instanceof Error ? err : new Error(String(err));
      this.store.setState({ fetchError: error, isLoading: false });
      this.options.onError?.(error);
    }
  }

  private fetchAudio(): void {
    const { content, selectedLanguage } = this.store.getState();
    if (!content?.notice_uuid || !this.ttsManager) return;
    void this.ttsManager.fetchAudioUrls({
      noticeUuid: content.notice_uuid,
      language: selectedLanguage,
      content,
    });
  }

  private viewContext(state: AssistedState, content: AssistedActiveConfig): AssistedViewContext {
    const isMobile = this.mediaQuery.matches;
    // Host settings, then the console palette, then the notice's brand colours.
    const look = buildAssistedLook({
      noticeId: this.options.noticeUuid,
      content,
      settings: this.options.settings,
      prefersDark: this.colorScheme.matches,
      isMobile,
    });
    return {
      state,
      content,
      isMobile,
      look,
      styles: look.styles,
      responsive: look.responsive,
      displayedPurposes: purposesInRenderOrder(
        content.products,
        content.purposes,
        content.product_purpose_order
      ),
      productPolicyLinks: getProductPolicyLinks(content.products, content.product_privacy_policies),
      primaryColor: look.primaryColor,
      linkColor: look.linkColor,
      accentTint: look.accentTint,
      t: (key, fallback, itemId) => this.translate(key, fallback, itemId),
      tDpo: (key, fallback) => this.translateDpo(key, fallback),
      ts: this.ts(),
      handlers: this.handlers,
    };
  }

  private handleStateChange(state: AssistedState, prev: AssistedState): void {
    const changed = (Object.keys(state) as Array<keyof AssistedState>).filter(
      (key) => state[key] !== prev[key]
    );
    if (changed.length === 0) return;
    if (state.content && changed.every((key) => PATCHABLE_KEYS.has(key)) && this.patch(state, changed)) {
      return;
    }
    this.render();
  }

  private patch(state: AssistedState, changed: Array<keyof AssistedState>): boolean {
    const root = this.shadowRoot;
    if (!root || !state.content) return false;
    const ctx = this.viewContext(state, state.content);

    if (changed.includes("mobile")) {
      const input = root.getElementById(ASSISTED_IDS.mobileInput) as HTMLInputElement | null;
      if (input && input.value !== state.mobile) input.value = state.mobile;
    }

    if (changed.includes("otpDigits")) {
      state.otpDigits.forEach((digit, index) => {
        const box = root.getElementById(otpInputId(index)) as HTMLInputElement | null;
        if (!box) return;
        if (box.value !== digit) box.value = digit;
        applyStyles(box, otpBoxStyle(ctx, digit, index));
      });
      if (this.pendingFocusId) {
        (root.getElementById(this.pendingFocusId) as HTMLElement | null)?.focus();
        this.pendingFocusId = null;
      }
    }

    if (changed.includes("resendCountdown")) {
      const resend = root.getElementById(ASSISTED_IDS.resend);
      if (resend) resend.replaceWith(renderResend(ctx));
    }

    if (changed.includes("ttsRevision")) {
      const slot = root.getElementById(ASSISTED_IDS.speakerSlot);
      if (slot) {
        clearElement(slot);
        const speaker = this.renderSpeaker(ctx);
        if (speaker) slot.appendChild(speaker);
      }
    }

    const primary = root.querySelector<HTMLButtonElement>(`[${FOCUS_KEY_ATTRIBUTE}="${FOCUS_KEYS.primary}"]`);
    if (primary) {
      const next = primaryAction(ctx);
      primary.disabled = next.disabled;
      if (primary.textContent !== next.label) primary.textContent = next.label;
    }
    return true;
  }

  private focusTarget(): { key: string | null; id: string | null; caret: number | null } {
    const active = this.shadowRoot?.activeElement as HTMLInputElement | null;
    if (this.pendingFocusId) {
      const id = this.pendingFocusId;
      this.pendingFocusId = null;
      return { key: null, id, caret: null };
    }
    if (!active) return { key: null, id: null, caret: null };
    return {
      key: active.getAttribute(FOCUS_KEY_ATTRIBUTE),
      id: active.id || null,
      caret: active.type === "tel" || active.type === "text" ? active.selectionStart : null,
    };
  }

  private restoreFocus(target: { key: string | null; id: string | null; caret: number | null }): void {
    const root = this.shadowRoot;
    if (!root) return;
    const element = (
      target.key
        ? root.querySelector(`[${FOCUS_KEY_ATTRIBUTE}="${target.key}"]`)
        : target.id
          ? root.getElementById(target.id)
          : null
    ) as HTMLInputElement | null;
    if (!element) return;
    element.focus();
    if (target.caret !== null && (element.type === "tel" || element.type === "text")) {
      element.setSelectionRange(target.caret, target.caret);
    }
  }

  private render(): void {
    if (this.destroyed || !this.modalEl || !this.shadowRoot) return;
    const state = this.store.getState();
    const focus = this.focusTarget();
    const scrollTop = this.middleEl?.scrollTop ?? 0;

    this.removeLanguageDropdown();
    clearElement(this.modalEl);
    const ts = this.ts();
    const body = el("div", { style: styles.content });
    this.modalEl.appendChild(body);
    this.middleEl = null;

    if (state.isLoading) {
      body.appendChild(
        el(
          "div",
          { style: styles.loadingContainer },
          el("div", { style: styles.loadingSpinner, role: "status", "aria-label": ts("loadingNotice") })
        )
      );
      return;
    }

    if (state.fetchError || !state.content) {
      body.appendChild(
        el(
          "div",
          { style: { ...styles.loadingContainer, gap: "8px" } },
          el("p", { style: assistedStyles.errorTitle }, ts("loadFailedTitle")),
          el("p", { style: assistedStyles.errorText }, state.fetchError?.message || ts("tryAgain")),
          el(
            "button",
            {
              type: "button",
              style: { ...styles.button, ...styles.cancelButton, width: "auto" },
              [FOCUS_KEY_ATTRIBUTE]: FOCUS_KEYS.close,
              onClick: () => this.handleDecline(),
            },
            ts("close")
          )
        )
      );
      return;
    }

    const content = state.content;
    const ctx = this.viewContext(state, content);
    const { look } = ctx;
    const noticeTitle = ctx.t("notice_banner_heading", content.notice_banner_heading || "Privacy Notice");
    this.modalEl.setAttribute("aria-label", noticeTitle);
    for (const [name, value] of Object.entries(look.rootAttributes)) {
      this.modalEl.setAttribute(name, value);
    }
    this.modalEl.removeAttribute("style");
    applyStyles(this.modalEl, {
      ...look.styles.modal,
      width: ctx.isMobile ? "90%" : "700px",
      fontFamily: resolveNoticeFontFamily({
        hostFont: this.options.settings?.font,
        noticeFont: content.font_preference,
      }),
    });
    applyCssVariables(this.modalEl, look.rootVariables);
    if (this.overlayEl) {
      this.overlayEl.style.backgroundColor = String(look.styles.overlay.backgroundColor);
    }
    if (this.shadowRoot) {
      this.customCss.apply(this.shadowRoot, this.options.noticeUuid, look.appearance.customCss);
    }
    body.style.margin = ctx.isMobile ? "20px" : "22px";

    body.appendChild(this.renderHeader(ctx, noticeTitle));
    const isVerify = state.step === "verify";
    this.middleEl = el(
      "div",
      { style: { ...look.styles.middleSection, paddingRight: ctx.isMobile ? "10px" : "15px" } },
      renderNoticeText(ctx),
      renderPurposeHeading(ctx),
      renderPurposes(ctx, isVerify),
      renderDpoFooter(ctx),
      isVerify ? renderVerifyPanel(ctx) : null
    );
    body.appendChild(this.middleEl);
    body.appendChild(renderFooter(ctx));
    this.middleEl.scrollTop = scrollTop;
    this.restoreFocus(focus);

    if (this.pendingReveal) {
      const reveal = this.pendingReveal;
      this.pendingReveal = null;
      setTimeout(() => scrollIntoViewSoft(this.shadowRoot?.getElementById(reveal) ?? null), 60);
    }
  }

  private renderSpeaker(ctx: AssistedViewContext): HTMLElement | null {
    const tts = this.ttsManager;
    if (!tts?.isAvailable || ctx.state.step !== "notice") return null;
    const playing = tts.isPlaying && !tts.isPaused;
    return el(
      "button",
      {
        type: "button",
        style: {
          ...ctx.styles.topRight,
          ...ctx.responsive.topRight,
          ...(ctx.look.appearance.colors.language_bg
            ? { backgroundColor: ctx.look.appearance.colors.language_bg }
            : {}),
          ...(ctx.look.appearance.colors.language_text
            ? { color: ctx.look.appearance.colors.language_text }
            : {}),
          padding: "3px 9px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        "aria-label": playing ? ctx.ts("audioPause") : ctx.ts("audioStart"),
        title: playing ? ctx.ts("talkbackPause") : ctx.ts("talkbackStart"),
        [FOCUS_KEY_ATTRIBUTE]: FOCUS_KEYS.speaker,
        onClick: () => this.ttsManager?.toggle(),
      },
      speakerIcon(playing)
    );
  }

  private renderHeader(ctx: AssistedViewContext, noticeTitle: string): HTMLElement {
    const { content, ts, isMobile, state, styles, responsive, look } = ctx;
    const title = el(
      "h2",
      {
        id: "privacy-notice-title",
        ...part("title"),
        style: { ...styles.title, ...responsive.title },
      },
      noticeTitle
    );
    const logo = content.logo_url
      ? el("img", {
          ...part("logo"),
          src: content.logo_url,
          alt: ts("logoAlt"),
          style: { ...styles.logo, ...responsive.logo },
        })
      : null;

    const speakerSlot = el("span", { id: ASSISTED_IDS.speakerSlot, style: { display: "flex" } });
    const speaker = this.renderSpeaker(ctx);
    if (speaker) speakerSlot.appendChild(speaker);

    const colors = look.appearance.colors;
    this.langSelectorRefs = renderLanguageSelector({
      languages: availableLanguages(content.default_language, content.supported_languages_and_translations),
      selectedLanguage: state.selectedLanguage,
      look: {
        styles,
        responsive,
        chip: {
          ...(colors.language_bg ? { backgroundColor: colors.language_bg } : {}),
          ...(colors.language_text ? { color: colors.language_text } : {}),
          ...(colors.border ? { borderColor: colors.border } : {}),
        },
        menu: {},
        selected: {
          ...(colors.language_selected_bg ? { backgroundColor: colors.language_selected_bg } : {}),
          ...(colors.language_selected_text ? { color: colors.language_selected_text } : {}),
        },
        rootAttributes: look.rootAttributes,
      },
      onSelect: (lang) => this.handleLanguageSelect(lang),
      dropdownContainer: this.shadowRoot as ShadowRoot,
    });
    this.langSelectorRefs.button.setAttribute(FOCUS_KEY_ATTRIBUTE, FOCUS_KEYS.language);

    const controls = el(
      "div",
      { style: { display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 } },
      speakerSlot,
      this.langSelectorRefs.container
    );

    // Centred or right: the logo takes its own row above the header, as it
    // does in the modal. Left keeps it beside the title, as it always sat.
    const logoRow =
      logo && look.logoPosition !== "left"
        ? el(
            "div",
            { style: { ...styles.logoRow, justifyContent: logoRowJustify(look.logoPosition) } },
            logo
          )
        : null;
    const headerLogo = logoRow ? null : logo;

    const sectionStyle = {
      ...styles.topSection,
      flexDirection: isMobile ? "column" : "row",
      alignItems: isMobile ? "stretch" : "flex-start",
      gap: isMobile ? "12px" : "10px",
    };

    const header = isMobile
      ? el(
          "div",
          { style: sectionStyle },
          el(
            "div",
            { style: { display: "flex", alignItems: "center", gap: "10px", width: "100%" } },
            headerLogo,
            el("div", { style: { marginLeft: "auto", display: "flex", alignItems: "center", flexShrink: 0 } }, controls)
          ),
          title
        )
      : el("div", { style: sectionStyle }, el("div", { style: styles.topLeft }, headerLogo, title), controls);

    if (!logoRow) {
      header.setAttribute("data-redacto-part", "header");
      return header;
    }
    return el(
      "div",
      { ...part("header"), style: { display: "flex", flexDirection: "column", gap: "10px" } },
      logoRow,
      header
    );
  }

  private removeLanguageDropdown(): void {
    const refs = this.langSelectorRefs;
    if (!refs) return;
    refs.close();
    if (this.shadowRoot && refs.dropdown.parentNode === this.shadowRoot) {
      this.shadowRoot.removeChild(refs.dropdown);
    }
    this.langSelectorRefs = null;
  }

  private handleLanguageSelect(lang: string): void {
    this.store.setState({ selectedLanguage: lang });
    this.fetchAudio();
  }

  private handlePurposeToggle(purposeUuid: string): void {
    const state = this.store.getState();
    const purpose = state.content?.purposes.find((p) => p.uuid === purposeUuid);
    if (!purpose || state.step !== "notice") return;
    this.store.setState(togglePurposeSelection(purpose, state));
  }

  private handleDataElementToggle(purposeUuid: string, elementUuid: string): void {
    const state = this.store.getState();
    const purpose = state.content?.purposes.find((p) => p.uuid === purposeUuid);
    if (!purpose || state.step !== "notice") return;
    this.store.setState(toggleDataElementSelection(purpose, elementUuid, state));
  }

  private handlePurposeCollapse(purposeUuid: string): void {
    const { collapsedPurposes } = this.store.getState();
    this.store.setState({
      collapsedPurposes: { ...collapsedPurposes, [purposeUuid]: !collapsedPurposes[purposeUuid] },
    });
  }

  private handleConfirmStep(mode: AcceptMode): void {
    const state = this.store.getState();
    const content = state.content;
    if (!content || state.step !== "notice") return;
    // A purpose that covers no product on the notice is not shown, so it
    // cannot be ticked and must not hold the step shut.
    const shown = purposesInRenderOrder(
      content.products,
      content.purposes,
      content.product_purpose_order
    );
    if (mode === "selected" && !areAllRequiredElementsChecked(shown, state.selectedDataElements)) {
      return;
    }
    const selection = selectionForMode(content.purposes, mode, state);
    this.ttsManager?.stop();
    this.pendingFocusId =
      state.verifyMethod === "email" ? ASSISTED_IDS.emailInput : ASSISTED_IDS.mobileInput;
    this.pendingReveal = ASSISTED_IDS.verifyPanel;
    this.store.setState({
      selectedPurposes: selection.selectedPurposes,
      selectedDataElements: selection.selectedDataElements,
      acceptMode: mode,
      step: "verify",
      otpSent: false,
    });
  }

  private discardOtpSession(): void {
    this.otpRequest += 1;
    this.verified = null;
    this.stopCountdown();
  }

  private handleEditSelections(): void {
    this.discardOtpSession();
    this.store.setState({
      acceptMode: "selected",
      step: "notice",
      otpSent: false,
      otpUuid: "",
      mobile: "",
      email: "",
      otpDigits: emptyOtpDigits(),
      resendCountdown: 0,
      isSendingOtp: false,
      createError: null,
      otpError: null,
    });
  }

  private handleSwitchMethod(): void {
    const state = this.store.getState();
    if (state.otpSent || state.isSendingOtp) return;
    const verifyMethod = state.verifyMethod === "email" ? "mobile" : "email";
    this.pendingFocusId = verifyMethod === "email" ? ASSISTED_IDS.emailInput : ASSISTED_IDS.mobileInput;
    this.store.setState({ verifyMethod, createError: null });
  }

  private handleGetOtp(): void {
    const state = this.store.getState();
    if (state.isSendingOtp || !isContactValid(state.verifyMethod, state.mobile, state.email)) return;
    void this.sendOtp();
  }

  private handleResend(): void {
    if (this.store.getState().isSendingOtp) return;
    void this.sendOtp();
  }

  private async sendOtp(): Promise<void> {
    const state = this.store.getState();
    const request = ++this.otpRequest;
    this.verified = null;
    this.store.setState({ createError: null, isSendingOtp: true });
    try {
      const detail = await createOtp({
        organisationUuid: this.options.organisationUuid,
        workspaceUuid: this.options.workspaceUuid,
        to: otpRecipient(state.verifyMethod, state.mobile, state.email),
        channel: otpChannel(state.verifyMethod),
        baseUrl: this.endpoints.baseUrl,
        signal: this.abortController.signal,
      });
      if (this.destroyed || request !== this.otpRequest) return;
      this.pendingFocusId = otpInputId(0);
      this.pendingReveal = otpInputId(0);
      this.store.setState({
        otpUuid: detail.uuid,
        otpDigits: emptyOtpDigits(),
        otpError: null,
        resendCountdown: ASSISTED_CONFIG.resendSeconds,
        otpSent: true,
        isSendingOtp: false,
      });
      this.startCountdown();
    } catch {
      if (this.destroyed || request !== this.otpRequest) return;
      this.store.setState({ createError: this.ts()("otpSendFailed"), isSendingOtp: false });
    }
  }

  private startCountdown(): void {
    this.stopCountdown();
    this.resendTimer = setInterval(() => {
      const remaining = this.store.getState().resendCountdown;
      if (remaining <= 1) {
        this.stopCountdown();
        this.store.setState({ resendCountdown: 0 });
        return;
      }
      this.store.setState({ resendCountdown: remaining - 1 });
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }

  private handleChangeContact(): void {
    const state = this.store.getState();
    this.discardOtpSession();
    this.pendingFocusId =
      state.verifyMethod === "email" ? ASSISTED_IDS.emailInput : ASSISTED_IDS.mobileInput;
    this.store.setState({
      otpSent: false,
      otpUuid: "",
      otpDigits: emptyOtpDigits(),
      resendCountdown: 0,
      isSendingOtp: false,
      createError: null,
      otpError: null,
    });
  }

  private handleOtpInput(index: number, value: string): void {
    const next = applyOtpDigit(this.store.getState().otpDigits, index, value);
    this.pendingFocusId = next.focusIndex !== null ? otpInputId(next.focusIndex) : null;
    this.store.setState({ otpDigits: next.digits });
    this.pendingFocusId = null;
  }

  private handleOtpKeydown(index: number, event: KeyboardEvent): void {
    if (event.key !== "Backspace") return;
    const target = backspaceFocusIndex(this.store.getState().otpDigits, index);
    if (target === null) return;
    (this.shadowRoot?.getElementById(otpInputId(target)) as HTMLInputElement | null)?.focus();
  }

  private handleOtpPaste(event: ClipboardEvent): void {
    const pasted = applyOtpPaste(event.clipboardData?.getData("text") ?? "");
    if (!pasted) return;
    event.preventDefault();
    this.pendingFocusId = otpInputId(pasted.focusIndex);
    this.store.setState({ otpDigits: pasted.digits });
    this.pendingFocusId = null;
  }

  private async verifiedToken(otpUuid: string, code: string): Promise<string> {
    if (this.verified?.otpUuid === otpUuid) return this.verified.accessToken;
    const detail = await verifyOtp({
      organisationUuid: this.options.organisationUuid,
      workspaceUuid: this.options.workspaceUuid,
      uuid: otpUuid,
      otp: code,
      purpose: ASSISTED_CONFIG.otpPurpose,
      baseUrl: this.endpoints.baseUrl,
      signal: this.abortController.signal,
    });
    this.verified = { otpUuid, accessToken: detail.access_token };
    return detail.access_token;
  }

  private async handleConfirmOtp(): Promise<void> {
    const state = this.store.getState();
    const content = state.content;
    if (!content || state.isVerifying || !isOtpComplete(state.otpDigits)) return;
    const ts = this.ts();
    this.store.setState({ otpError: null, isVerifying: true });

    let accessToken: string;
    try {
      accessToken = await this.verifiedToken(state.otpUuid, state.otpDigits.join(""));
    } catch (err) {
      if (this.destroyed) return;
      this.store.setState({ otpError: ts(verifyFailureKey(err)), isVerifying: false });
      return;
    }
    if (this.destroyed) return;

    const noticeUuid = content.notice_uuid || this.options.noticeUuid;
    try {
      if (this.endpoints.ledgerBaseUrl) {
        const status = await fetchNoticeConsentStatusForScope({
          accessToken,
          ledgerBaseUrl: this.endpoints.ledgerBaseUrl,
          organisationUuid: this.options.organisationUuid,
          workspaceUuid: this.options.workspaceUuid,
          noticeUuid,
          signal: this.abortController.signal,
        });
        if (this.destroyed) return;
        if (status && isNoticeFullyConsented(status)) {
          this.complete();
          return;
        }
      }

      const latest = this.store.getState();
      const selection = selectionForMode(content.purposes, latest.acceptMode, latest);
      await submitAssistedConsent({
        accessToken,
        organisationUuid: this.options.organisationUuid,
        workspaceUuid: this.options.workspaceUuid,
        baseUrl: this.endpoints.baseUrl,
        ledgerBaseUrl: this.endpoints.ledgerBaseUrl,
        noticeUuid,
        purposes: buildAssistedSubmitPurposes(content.purposes, selection),
        language: latest.selectedLanguage,
        signal: this.abortController.signal,
      });
      if (this.destroyed) return;
      this.complete();
    } catch (err) {
      if (this.destroyed) return;
      if (isAlreadyConsentedError(err)) {
        this.complete();
        return;
      }
      this.store.setState({ otpError: ts("tryAgain"), isVerifying: false });
      this.options.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  }

  private complete(): void {
    this.options.onComplete?.();
    this.destroy();
  }

  private handleDecline(): void {
    this.options.onDecline?.();
    this.destroy();
  }
}
